import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Divider, Grid, Typography } from '@mui/material';

import { FBoxContainer, FHInputField, FProgressBar } from '../../../base';
import { clientFamilyBackgroundActions } from '../../../../store';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { FORTA_BLUE } from '../../../../utils/colors';
import ParentsCaregivers from './ParentsCaregivers';
import Siblings from './Siblings';
import MemberLanguage from './MemberLanguage';
import Strengths from './Strengths';
import Interests from './Interests';
import FamilyBackground from './FamilyBackground';
import { TreatmentPlanResourceVersion } from '../constants/general';

import type { ChangeEvent } from 'react';

function ClientFamilyBackground() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const {
    clientFamilyBackground,
    loading,
    treatmentPlanId: currentTPId,
  } = useSelector((state: any) => state.clientFamilyBackgrounds);
  const backgroundVersion = treatmentPlan?.resource_versions?.client_and_family_background;
  const [other, setOther] = useState<string>('');

  useEffect(() => {
    if (!treatmentPlan) {
      dispatch(clientFamilyBackgroundActions.clearState());
      return;
    }
    if (currentTPId !== treatmentPlan?.id) {
      dispatch(clientFamilyBackgroundActions.clearState());
    }
    // Get editing client family background instead of fetching api.
    if (clientFamilyBackground?.id && currentTPId === treatmentPlan?.id) return;

    fetchClientFamilyBackground();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan]);

  useEffect(() => {
    if (
      !treatmentPlan ||
      !clientFamilyBackground ||
      !clientFamilyBackground['other_individuals_in_home']
    ) {
      setOther('');
      return;
    }

    setOther(clientFamilyBackground['other_individuals_in_home']);
  }, [clientFamilyBackground, treatmentPlan]);

  const fetchClientFamilyBackground = async () => {
    dispatch(
      clientFamilyBackgroundActions.getClientFamilyBackground({
        treatmentPlanId: treatmentPlan.id,
        getAccessTokenSilently,
      }),
    );
  };

  const handleSave = async () => {
    if (!treatmentPlan || !clientFamilyBackground) return;
    if (!clientFamilyBackground?.id) {
      const response = await dispatch(
        clientFamilyBackgroundActions.createClientFamilyBackground({
          treatmentPlanId: treatmentPlan?.id,
          data: {
            other_individuals_in_home: other,
          },
          getAccessTokenSilently,
        }),
      );
      if (response.error) {
        return;
      }
    } else {
      dispatch(
        clientFamilyBackgroundActions.updateClientFamilyBackground({
          treatmentPlanId: treatmentPlan.id,
          data: {
            ...clientFamilyBackground,
            other_individuals_in_home: other,
          },
          getAccessTokenSilently,
        }),
      );
    }
  };

  return (
    <FBoxContainer title="Client and family background" sx={{ mt: 3 }}>
      {loading && <FProgressBar />}
      <MemberLanguage />
      <Divider
        sx={{ borderWidth: 1.5, borderColor: FORTA_BLUE.primary, mb: 3, mt: 3, borderRadius: 3 }}
      />
      {backgroundVersion === TreatmentPlanResourceVersion.V2 && (
        <>
          <Strengths />
          <Divider
            sx={{
              borderWidth: 1.5,
              borderColor: FORTA_BLUE.primary,
              mb: 3,
              mt: 3,
              borderRadius: 3,
            }}
          />
          <Interests />
          <Divider
            sx={{
              borderWidth: 1.5,
              borderColor: FORTA_BLUE.primary,
              mb: 3,
              mt: 3,
              borderRadius: 3,
            }}
          />
          <FamilyBackground />
          <Divider
            sx={{
              borderWidth: 1.5,
              borderColor: FORTA_BLUE.primary,
              mb: 3,
              mt: 3,
              borderRadius: 3,
            }}
          />
        </>
      )}
      <ParentsCaregivers />
      <Divider
        sx={{ borderWidth: 1.5, borderColor: FORTA_BLUE.primary, mb: 3, mt: 3, borderRadius: 3 }}
      />
      <Siblings />
      <Box mt={3}>
        <Typography fontWeight="bold" textAlign={'left'} mb={1}>
          Other individuals residing in the home:
        </Typography>
        <Grid container spacing={2} mb={1}>
          <Grid item xs={6}>
            <FHInputField
              data-testid="otherInduvidualsTextField"
              fullWidth
              disabled={!isEditable}
              value={other}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setOther(e.target.value)}
              onBlur={handleSave}
              inputHeight={40}
            />
          </Grid>
        </Grid>
      </Box>
    </FBoxContainer>
  );
}

export default ClientFamilyBackground;
